/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { Dropdown } from "semantic-ui-react";
import { TextField } from "@mui/material";
import { handleWheel, handleKeyDown } from "../functions/eventHandlers";
import { alertIfInvalidNumberEntry, displayStringFromFHIRQuantity } from "./QuantityEntry";
import StringEntry from "./StringEntry";
import ExpandToAddOrEdit from "../components/ExpandToAddOrEdit";
import UCUM from "../dictionaries/UCUM";

const displayStringFromFHIRRange = (range) => {
  if (range === undefined || range === null || range === '') {
    return ""
  }
  if (typeof range === "object") {
    try {
      let low = range.low || null;
      let high = range.high || null;
      let lowUnit = "";
      if (low && typeof (low) === 'object') {
        lowUnit = low.unit || low.code || ""
      }
      let highUnit = "";
      if (high && typeof (high) === 'object') {
        highUnit = high.unit || high.code || ""
      }
      if (low && high && lowUnit === highUnit && typeof (low) === 'object') {
        low = { ...low, unit: "", code: "" };
      }
      let lowDisplay = displayStringFromFHIRQuantity(low);
      let highDisplay = displayStringFromFHIRQuantity(high);
      if (lowDisplay || highDisplay) {
        return `${low ? lowDisplay : "No Lower Bound"} to ${high ? highDisplay : "No Upper Bound"}`;
      } else {
        return ""
      }
    } catch (e) {
      console.log("ERROR for displayStringFromFHIRRange for display of:");
      console.log(range);
      console.log(e);
    }
  }
  if (typeof range === "string") {
    return `${range} [ERROR: Range data not in expected Range datatype.]`
  }
  return "[ERROR: Range data not interpretable.]";
}

const RangeEntry = ({ elementName, fieldLabel, startingValue, setResourceState,
  dataEntryStyle, valueSet, inTableCell, startCollapsed }) => {

  if (dataEntryStyle === "Age") {
    valueSet = UCUM.age;
  } else if (dataEntryStyle === "Duration") {
    valueSet = UCUM.duration;
  } else if (valueSet === "UCUM") {
    valueSet = UCUM.common;
  } else if (valueSet === "UCUMplusUNL/LNL") {
    valueSet = UCUM.common.concat([{
      "system": "http://hl7.org/fhir/characteristic-offset",
      "code": "UNL",
      "display": "Upper Normal Limit"
    }, {
      "system": "http://hl7.org/fhir/characteristic-offset",
      "code": "LNL",
      "display": "Lower Normal Limit"
    }]);
  }

  const [rangePresetState, setRangePresetState] = useState({ lowEdited: false, highEdited: false });
  const [rangeState, setRangeState] = useState({});
  const [startCollapsedState, setStartCollapsedState] = useState(startCollapsed);
  const [useLowUnitsForHighUnits, setUseLowUnitsForHighUnits] = useState(false);

  useEffect(() => {
    let startingRangeValues = {
      lowValue: "", lowUnit: "", lowSystem: "", lowCode: "",
      highValue: "", highUnit: "", highSystem: "", highCode: ""
    };
    if (startingValue) {
      if (startingValue.extension) { startingRangeValues.extension = startingValue.extension; }
      if (startingValue.low) {
        if (startingValue.low.value || startingValue.low.value === 0) {
          startingRangeValues.lowValue = startingValue.low.value;
        }
        if (startingValue.low.unit) { startingRangeValues.lowUnit = startingValue.low.unit; }
        if (startingValue.low.system) { startingRangeValues.lowSystem = startingValue.low.system; }
        if (startingValue.low.code) { startingRangeValues.lowCode = startingValue.low.code; }
      }
      if (startingValue.high) {
        if (startingValue.high.value || startingValue.high.value === 0) {
          startingRangeValues.highValue = startingValue.high.value;
        }
        if (startingValue.high.unit) { startingRangeValues.highUnit = startingValue.high.unit; }
        if (startingValue.high.system) { startingRangeValues.highSystem = startingValue.high.system; }
        if (startingValue.high.code) { startingRangeValues.highCode = startingValue.high.code; }
      }
    }
    setRangeState(startingRangeValues);
  }, []);

  useEffect(() => {
    if (Object.keys(rangeState).length) {
      let newRange = {};
      if (rangeState.extension) { newRange.extension = rangeState.extension; }
      if (rangeState.lowValue || rangeState.lowValue === 0) {
        newRange.low = { "value": rangeState.lowValue };
        if (rangeState.lowUnit) { newRange.low.unit = rangeState.lowUnit; }
        if (rangeState.lowSystem) { newRange.low.system = rangeState.lowSystem; }
        if (rangeState.lowCode) { newRange.low.code = rangeState.lowCode; }
      }
      if (rangeState.highValue || rangeState.highValue === 0) {
        newRange.high = { "value": rangeState.highValue };
        if (rangeState.highUnit) { newRange.high.unit = rangeState.highUnit; }
        if (rangeState.highSystem) { newRange.high.system = rangeState.highSystem; }
        if (rangeState.highCode) { newRange.high.code = rangeState.highCode; }
      }
      if (Object.keys(newRange).length === 0) {
        newRange = null;
      }
      setResourceState(prevState => { return { ...prevState, [elementName]: newRange } });
    }
  }, [rangeState]);

  useEffect(() => {
    if (useLowUnitsForHighUnits) {
      setRangeState(prevState => {
        return {
          ...prevState,
          "highUnit": rangeState.lowUnit,
          "highSystem": rangeState.lowSystem,
          "highCode": rangeState.lowCode
        }
      });
      setUseLowUnitsForHighUnits(false);
    }
  }, [useLowUnitsForHighUnits]);

  if (startCollapsedState) {
    return <>
      <p>
        <b>{fieldLabel}: </b>
        {startingValue && displayStringFromFHIRRange(startingValue)}
        &nbsp;&nbsp;
        <ExpandToAddOrEdit startingValue={startingValue} setStartCollapsedState={setStartCollapsedState} />
      </p>
    </>
  } else if (Object.keys(rangeState).length > 0) {
    if (!valueSet || !Array.isArray(valueSet)) {
      return <div><p style={{ marginBottom: "2px" }}><b>{fieldLabel}: </b></p>
        <TextField style={{ width: "15%" }} className="inputField" type='number' inputMode='numeric'
          autoComplete='off'
          label={'Low Value'}
          size="small" variant='outlined' value={rangeState.lowValue ?? ''}
          onWheel={handleWheel}
          onKeyDown={handleKeyDown}
          onInput={alertIfInvalidNumberEntry}
          onChange={(e) => {
            setRangeState(prevState => { return { ...prevState, "lowValue": parseFloat(e.target.value) }; });
          }} />
        &nbsp;&nbsp;&nbsp;
        <StringEntry elementName="lowUnit" fieldLabel="Low Unit"
          startingValue={rangeState.lowUnit} setResourceState={setRangeState} />
        &nbsp;&nbsp;&nbsp;
        <br />
        <TextField style={{ width: "15%" }} className="inputField" type='number' inputMode='numeric'
          autoComplete='off'
          label={'High Value'}
          size="small" variant='outlined' value={rangeState.highValue ?? ''}
          onWheel={handleWheel}
          onKeyDown={handleKeyDown}
          onInput={alertIfInvalidNumberEntry}
          onChange={(e) => {
            setRangeState(prevState => { return { ...prevState, "highValue": parseFloat(e.target.value) }; });
          }} />
        &nbsp;&nbsp;&nbsp;
        <span onClick={() => { setUseLowUnitsForHighUnits(true) }}>Click to use same units as low.</span>
        &nbsp;&nbsp;&nbsp;
        <StringEntry elementName="highUnit" fieldLabel="High Unit"
          startingValue={rangeState.highUnit} setResourceState={setRangeState} />
      </div>
    }

    if (dataEntryStyle === "Age" || dataEntryStyle === "Duration") {

      return <div><p style={{ marginBottom: "2px" }}><b>{fieldLabel}: </b></p>
        <span>
          <TextField style={{ width: "15%" }} className="inputField" type='number' inputMode='numeric'
            autoComplete='off'
            label={'Low Value'}
            size="small" variant='outlined' value={rangeState.lowValue ?? ''}
            onWheel={handleWheel}
            onKeyDown={handleKeyDown}
            onInput={alertIfInvalidNumberEntry}
            onChange={(e) => {
              setRangeState(prevState => { return { ...prevState, "lowValue": parseFloat(e.target.value) }; });
            }} />
          &nbsp;&nbsp;&nbsp;
          <Dropdown name="timeunit" placeholder="Units of Time"
            closeOnChange selection selectOnBlur={false} autoComplete='off'
            style={{ minWidth: "22%", width: "22%" }}
            options={valueSet.map((term, termIndex) => { return { "key": termIndex, "value": term.code, "text": term.display } })}
            value={rangePresetState.lowEdited ? "" : startingValue?.low?.code}
            onChange={(e, data) => {
              let selectedCode;
              let selectedSystem;
              let selectedDisplay;
              for (const term of valueSet) {
                if (term.code === data.value) {
                  selectedCode = term.code;
                  selectedSystem = term.system;
                  selectedDisplay = term.display;
                }
              }
              setRangeState(prevState => {
                return {
                  ...prevState,
                  "lowUnit": selectedDisplay,
                  "lowSystem": selectedSystem,
                  "lowCode": selectedCode
                }
              });
            }}
          />
          &nbsp;&nbsp;&nbsp;
        </span>
        {inTableCell && <br />}
        <span>
          <TextField style={{ width: "15%" }} className="inputField" type='number' inputMode='numeric'
            autoComplete='off'
            label={'High Value'}
            size="small" variant='outlined' value={rangeState.highValue ?? ''}
            onWheel={handleWheel}
            onKeyDown={handleKeyDown}
            onInput={alertIfInvalidNumberEntry}
            onChange={(e) => {
              setRangeState(prevState => { return { ...prevState, "highValue": parseFloat(e.target.value) }; });
            }} />
          &nbsp;&nbsp;&nbsp;
          <Dropdown name="timeunit" placeholder="Units of Time"
            closeOnChange selection selectOnBlur={false} autoComplete='off'
            style={{ minWidth: "22%", width: "22%" }}
            options={valueSet.map((term, termIndex) => { return { "key": termIndex, "value": term.code, "text": term.display } })}
            value={rangePresetState.highEdited ? "" : startingValue?.high?.code}
            onChange={(e, data) => {
              let selectedCode;
              let selectedSystem;
              let selectedDisplay;
              for (const term of valueSet) {
                if (term.code === data.value) {
                  selectedCode = term.code;
                  selectedSystem = term.system;
                  selectedDisplay = term.display;
                }
              }
              setRangeState(prevState => {
                return {
                  ...prevState,
                  "highUnit": selectedDisplay,
                  "highSystem": selectedSystem,
                  "highCode": selectedCode
                }
              });
            }}
          />
        </span>
      </div>
    } else {
      return <div><p style={{ marginBottom: "2px" }}><b>{fieldLabel}: </b></p>
        <span>
          <TextField style={{ width: "15%" }} className="inputField" type='number' inputMode='numeric'
            autoComplete='off'
            label={'Low Value'}
            size="small" variant='outlined' value={rangeState.lowValue ?? ''}
            onWheel={handleWheel}
            onKeyDown={handleKeyDown}
            onInput={alertIfInvalidNumberEntry}
            onChange={(e) => {
              setRangeState(prevState => { return { ...prevState, "lowValue": parseFloat(e.target.value) }; });
            }} />
          &nbsp;&nbsp;&nbsp;
          <Dropdown
            name={"low-unit-picklist-for-" + elementName}
            placeholder="Select Units" search={true}
            closeOnChange selection clearable selectOnBlur={false} autoComplete='off'
            style={{ minWidth: "25%", width: "25%" }}
            options={valueSet.map((term, termIndex) => { return { "key": termIndex, "value": term.code, "text": term.display } })}
            value={rangePresetState.lowEdited ? "" : startingValue?.low?.code}
            onChange={(e, data) => {
              let selectedCode;
              let selectedSystem;
              let selectedDisplay;
              for (const term of valueSet) {
                if (term.code === data.value) {
                  selectedCode = term.code;
                  selectedSystem = term.system || "";
                  selectedDisplay = term.display || "";
                }
              }
              setRangeState(prevState => {
                return {
                  ...prevState,
                  "lowUnit": selectedDisplay,
                  "lowSystem": selectedSystem,
                  "lowCode": selectedCode
                }
              });
            }}
          />
          &nbsp;&nbsp;&nbsp;
          <TextField style={{ width: "25%" }} className="inputField" type='text' label={'System'}
            size="small" variant='outlined' value={rangeState.lowSystem || ''}
            onChange={(e) => {
              setRangeState(prevState => { return { ...prevState, "lowSystem": e.target.value }; });
              setRangePresetState((prev) => { return { ...prev, lowEdited: true } });
            }} />
          &nbsp;&nbsp;&nbsp;
          <TextField style={{ width: "25%" }} className="inputField" type='text' label={'Code'}
            size="small" variant='outlined' value={rangeState.lowCode || ''}
            onChange={(e) => {
              setRangeState(prevState => { return { ...prevState, "lowCode": e.target.value }; });
              setRangePresetState((prev) => { return { ...prev, lowEdited: true } });
            }} />
          &nbsp;&nbsp;&nbsp;
          <TextField style={{ width: "25%" }} className="inputField" type='text' label={'Unit Display'}
            size="small" variant='outlined' value={rangeState.lowUnit || ''}
            onChange={(e) => {
              setRangeState(prevState => { return { ...prevState, "lowUnit": e.target.value }; });
              setRangePresetState((prev) => { return { ...prev, lowEdited: true } });
            }} />
        </span>
        <br />
        <span onClick={() => { setUseLowUnitsForHighUnits(true) }}>Click to use same units as low.</span>
        <br />
        <span>
          <TextField style={{ width: "15%" }} className="inputField" type='number' inputMode='numeric'
            autoComplete='off'
            label={'High Value'}
            size="small" variant='outlined' value={rangeState.highValue ?? ''}
            onWheel={handleWheel}
            onKeyDown={handleKeyDown}
            onInput={alertIfInvalidNumberEntry}
            onChange={(e) => {
              setRangeState(prevState => { return { ...prevState, "highValue": parseFloat(e.target.value) }; });
            }} />
          &nbsp;&nbsp;&nbsp;
          <Dropdown
            name={"high-unit-picklist-for-" + elementName}
            placeholder="Select Units" search={true}
            closeOnChange selection clearable selectOnBlur={false} autoComplete='off'
            style={{ minWidth: "25%", width: "25%" }}
            options={valueSet.map((term, termIndex) => { return { "key": termIndex, "value": term.code, "text": term.display } })}
            value={rangePresetState.highEdited ? "" : startingValue?.high?.code}
            onChange={(e, data) => {
              let selectedCode;
              let selectedSystem;
              let selectedDisplay;
              for (const term of valueSet) {
                if (term.code === data.value) {
                  selectedCode = term.code;
                  selectedSystem = term.system || "";
                  selectedDisplay = term.display || "";
                }
              }
              setRangeState(prevState => {
                return {
                  ...prevState,
                  "highUnit": selectedDisplay,
                  "highSystem": selectedSystem,
                  "highCode": selectedCode
                }
              });
            }}
          />
          &nbsp;&nbsp;&nbsp;
          <TextField style={{ width: "25%" }} className="inputField" type='text' label={'System'}
            size="small" variant='outlined' value={rangeState.highSystem || ''}
            onChange={(e) => {
              setRangeState(prevState => { return { ...prevState, "highSystem": e.target.value }; });
              setRangePresetState((prev) => { return { ...prev, highEdited: true } });
            }} />
          &nbsp;&nbsp;&nbsp;
          <TextField style={{ width: "25%" }} className="inputField" type='text' label={'Code'}
            size="small" variant='outlined' value={rangeState.highCode || ''}
            onChange={(e) => {
              setRangeState(prevState => { return { ...prevState, "highCode": e.target.value }; });
              setRangePresetState((prev) => { return { ...prev, highEdited: true } });
            }} />
          &nbsp;&nbsp;&nbsp;
          <TextField style={{ width: "25%" }} className="inputField" type='text' label={'Unit Display'}
            size="small" variant='outlined' value={rangeState.highUnit || ''}
            onChange={(e) => {
              setRangeState(prevState => { return { ...prevState, "highUnit": e.target.value }; });
              setRangePresetState((prev) => { return { ...prev, highEdited: true } });
            }} />
        </span>
      </div>
    }
  }
};

export default RangeEntry;
export { displayStringFromFHIRRange };